
import { isBefore, parse } from 'date-fns';
import { Ticket } from '../models/Ticket';
import { IoCalendarClear, IoCard, IoTicket, IoAlert } from 'react-icons/io5';

/**
 * Enum definiujący wszystkie dostępne typy biletów/karnetów
 * 
 * OPEN (0):          - Bilet OPEN - bez limitu wejść dla karnetów, nielimitowane dla biletów
 * LIMITED (4):       - Bilet Limited - ograniczona liczba wejść, pojedynczy bilet (nie wymaga terminu)
 * CLASS_PASS (2,3):  - Bilet zajęciowy - bilet wymagający wyboru terminu zajęć
 *                      2 = single pass instance (select), 3 = multi-instance
 * CLUB_CARD (8):     - Karta Klubowa - członkowska karta klubu
 */
export enum PassType {
  /** Bilet OPEN - bez limitacji */
  OPEN = 0,
  
  /** Bilet Limited - limitowana liczba wejść */
  LIMITED = 4,
  
  /** Karnet zajęciowy - typ 1 */
  CLASS_PASS_SINGLE = 2,
  
  /** Karnet zajęciowy - typ 2 */
  CLASS_PASS_MULTI = 3,
  
  /** Karta Klubowa */
  CLUB_CARD = 8,

  /** Karnet szkolki pływackiej */
  SWIMMING_SCHOOL = 9,
}

/**
 * Stałe dla typów biletów
 */
export const PASS_TYPES = {
  OPEN: PassType.OPEN,
  LIMITED: PassType.LIMITED,
  CLASS_PASS: [PassType.CLASS_PASS_SINGLE, PassType.CLASS_PASS_MULTI] as const,
  CLUB_CARD: PassType.CLUB_CARD,
} as const;

/**
 * Konfiguracja dla każdego typu biletu zawierająca wszystkie właściwości
 */
interface PassTypeConfig {
  name: string;
  iconType: 'ticket' | 'calendar' | 'card' | 'alert';
  requiresExerciseSelection: boolean;
  displayType: 'ticket' | 'pass';
  description: string;
}

const PASS_TYPE_CONFIGS: Record<PassType, PassTypeConfig> = {
  [PassType.OPEN]: {
    name: "Bilet OPEN",
    iconType: "ticket",
    requiresExerciseSelection: false,
    displayType: "ticket",
    description: "Bilet bez limitu wejść",
  },
  [PassType.LIMITED]: {
    name: "Bilet Limited",
    iconType: "ticket",
    requiresExerciseSelection: false,
    displayType: "ticket",
    description: "Bilet z ograniczoną liczbą wejść",
  },
  [PassType.CLASS_PASS_SINGLE]: {
    name: "Bilet zajęciowy",
    iconType: "calendar",
    requiresExerciseSelection: true,
    displayType: "pass",
    description: "Karnet wymagający wyboru terminu",
  },
  [PassType.CLASS_PASS_MULTI]: {
    name: "Bilet zajęciowy",
    iconType: "calendar",
    requiresExerciseSelection: true,
    displayType: "pass",
    description: "Karnet wymagający wyboru terminu",
  },
  [PassType.CLUB_CARD]: {
    name: "Karta Klubowa",
    iconType: "card",
    requiresExerciseSelection: false,
    displayType: "pass",
    description: "Członkowska karta klubu",
  },
  [PassType.SWIMMING_SCHOOL]: {
    name: "Nauka pływania",
    iconType: "calendar",
    requiresExerciseSelection: true,
    displayType: "pass",
    description: "Karnet szkółki pływackiej",
  },
};

/**
 * Mapowanie typów ikon na komponenty
 */
export const ICON_COMPONENTS = {
  ticket: IoTicket,
  calendar: IoCalendarClear,
  card: IoCard,
  alert: IoAlert,
} as const;

/**
 * Zwraca typ ikony dla danego typu biletu
 */
const getIconTypeForPassType = (passtype: number): keyof typeof ICON_COMPONENTS => {
  switch (passtype) {
    case PassType.OPEN:
    case PassType.LIMITED:
      return 'ticket';
    case PassType.CLASS_PASS_SINGLE:
    case PassType.CLASS_PASS_MULTI:
      return 'calendar';
    case PassType.CLUB_CARD:
      return 'card';
    default:
      return 'ticket';
  }
};

/**
 * Sprawdza czy bilet/karnet jest wygasły
 */
export const isTicketExpired = (ticket: Ticket): boolean => {
  const ticketDate =
    typeof ticket.validto === 'number'
      ? new Date(ticket.validto)
      : parse(String(ticket.validto), 'dd.MM.yyyy', new Date());

  return isBefore(ticketDate, new Date());
};

/**
 * Filtruje bilety na aktywne i wygasłe
 */
export const filterTicketsByStatus = (tickets: Ticket[]) => {
  return {
    active: tickets.filter(ticket => !isTicketExpired(ticket)),
    expired: tickets.filter(ticket => isTicketExpired(ticket)),
  };
};

/**
 * Sprawdza czy dany typ to OPEN
 */
export const isOpenPass = (passtype: number): boolean => {
  return passtype === PassType.OPEN;
};

/**
 * Sprawdza czy dany typ to LIMITED
 */
export const isLimitedPass = (passtype: number): boolean => {
  return passtype === PassType.LIMITED;
};

/**
 * Sprawdza czy dany typ to CLASS_PASS (zarówno SINGLE jak i MULTI)
 */
export const isClassPass = (passtype: number): boolean => {
  return passtype === PassType.CLASS_PASS_SINGLE || passtype === PassType.CLASS_PASS_MULTI;
};

/**
 * Sprawdza czy dany typ to CLUB_CARD
 */
export const isClubCard = (passtype: number): boolean => {
  return passtype === PassType.CLUB_CARD;
};

export const isSwimmingSchool = (passtype: number | string): boolean => {
  return Number(passtype) === PassType.SWIMMING_SCHOOL;
};;

/**
 * Sprawdza czy dany typ to bilet (ticket === true w modelu)
 */
export const isTicketType = (pass: Ticket): boolean => {
  return pass.ticket === true;
};

/**
 * Sprawdza czy dany typ to karnet (ticket === false w modelu)
 */
export const isPassType = (pass: Ticket): boolean => {
  return pass.ticket === false;
};

/**
 * Sprawdza czy bilet/karnet wymaga wyboru terminu (exercise)
 * LIMITED nigdy nie wymaga terminu, nawet jeśli nie ma exercises
 * OPEN:
 *  - dla biletów (ticket === true) wymaga terminu
 *  - dla karnetów (ticket === false) NIE wymaga terminu
 * CLASS_PASS zawsze wymaga terminu
 */
export const requiresExerciseSelection = (pass: Ticket): boolean => {
  // LIMITED bilety nigdy nie wymagają terminu - mogą być używane bez rezerwacji
  if (isLimitedPass(pass.passtype)) {
    return false;
  }

  // OPEN:
  // - tylko bilety (ticket === true) wymagają wyboru terminu
  // - karnety OPEN (ticket === false) nie wymagają wyboru terminu
  if (isOpenPass(pass.passtype)) {
    return isTicketType(pass);
  }

  // CLASS_PASS wymagają terminu - trzeba wybrać zajęcia
  if (isClassPass(pass.passtype)) {
    return true;
  }

  // CLUB_CARD nigdy nie wymaga terminu
  return false;
};

/**
 * Sprawdza czy bilet/karnet jest nieaktywny (brak wybranego terminu)
 * Logika: bilet jest nieaktywny, jeśli wymaga exercisów ale ich nie ma
 */
export const isTicketInactive = (pass: Ticket): boolean => {
  // Jeśli nie wymaga exercise selection, nigdy nie jest nieaktywny
  if (!requiresExerciseSelection(pass)) {
    return false;
  }

  // W innym wypadku: sprawdź czy ma exercises
  return !pass.exercises || pass.exercises.length === 0;
};

/**
 * Zwraca typ ikony dla danego typu biletu (do użytku w PassTag)
 */
export const getPassTypeIconType = (passtype: number): keyof typeof ICON_COMPONENTS => {
  return getIconTypeForPassType(passtype);
};

/**
 * Zwraca typ ikony dla stanu biletu (do użytku w SinglePass EAInfoTile)
 */
export const getInfoTileIconType = (pass: Ticket): keyof typeof ICON_COMPONENTS => {
  // Jeśli nieaktywny (wymaga wyboru terminu), pokaż alert
  if (isTicketInactive(pass)) {
    return "alert";
  }

  // Jeśli kończą się wejścia, pokaż alert
  if (hasEntranceWarning(pass)) {
    return "alert";
  }

  // W innym wypadku typ ikony zależy od typu biletu
  return getIconTypeForPassType(pass.passtype);
};

/**
 * Zwraca kolor dla EAInfoTile
 */
export const getInfoTileColor = (pass: Ticket): "error" | "warning" | "primary" => {
  if (isTicketInactive(pass)) {
    // Dla karnetu zajęciowego (passtype 2, ticket false) używamy warning zamiast error
    const isClassPassCard = isPassType(pass) && pass.passtype === PassType.CLASS_PASS_SINGLE;
    return isClassPassCard ? "warning" : "error";
  }

  // Jeśli kończą się wejścia - ostrzeżenie (żółty)
  if (hasEntranceWarning(pass)) {
    return "warning";
  }

  return "primary";
};

/**
 * Zwraca etykietę (label) dla danego typu biletu/karnetu
 * Uwzględnia czy to bilet (ticket === true) czy karnet (ticket === false)
 */
export const getPassTypeLabel = (ticket: Ticket): string => {
  const config = PASS_TYPE_CONFIGS[ticket.passtype as PassType];
  const baseName = config?.name || "Bilet";
  
  // Karta Klubowa zawsze "Karta Klubowa" niezależnie od ticket
  if (isClubCard(ticket.passtype)) {
    return baseName;
  }
  
  // Jeśli ticket === true → "Bilet [typ]"
  // Jeśli ticket === false → "Karnet [typ]"
  if (ticket.ticket === true) {
    return baseName; // baseName już zawiera "Bilet [typ]"
  } else {
    // Zamień "Bilet" na "Karnet" w nazwie
    return baseName.replace("Bilet", "Karnet");
  }
};

/**
 * Zwraca etykietę przycisku akcji
 */
export const getActionButtonLabel = (pass: Ticket): string => {
  // Jeśli nieaktywny i to ERROR (nie karnet zajęciowy), przycisk "Wybierz termin"
  if (isTicketInactive(pass)) {
    const isClassPassCard = isPassType(pass) && pass.passtype === PassType.CLASS_PASS_SINGLE;
    if (!isClassPassCard) {
      return "Wybierz termin";
    }
  }

  // Poza przypadkiem "Wybierz termin" etykieta zależy od tego,
  // czy to faktycznie bilet (ticket === true) czy karnet (ticket === false)
  if (isTicketType(pass)) {
    return "Pokaż bilet";
  }

  // Dla karnetów i kart
  return "Pokaż karnet";
};

/**
 * Zwraca wariant przycisku akcji
 */
export const getActionButtonVariant = (pass: Ticket): "filled" | "outlined" => {
  // Jeśli nieaktywny i to ERROR (nie karnet zajęciowy), przycisk "filled" (primary)
  if (isTicketInactive(pass)) {
    const isClassPassCard = isPassType(pass) && pass.passtype === PassType.CLASS_PASS_SINGLE;
    if (!isClassPassCard) {
      return "filled";
    }
  }

  return "outlined";
};

/**
 * Zwraca informację o ilości wejść do wyświetlenia
 */
export const getEntranceInfo = (ticket: Ticket): string => {
  const entrancesLeft = ticket.entrances - ticket.exercises.length;

  // Jeśli OPEN (passtype 0)
  if (isOpenPass(ticket.passtype)) {
    return ticket.ticket ? "nielimitowane wejścia" : "bez limitu wejść";
  }

  // Jeśli bilet (ticket === true)
  if (isTicketType(ticket)) {
    if (isClassPass(ticket.passtype)) return "1 wejście";
    return `${ticket.entrances} wejść`;
  }

  // Karta Klubowa - nie pokazujemy ilości wejść (jak przy OPEN)
  if (isClubCard(ticket.passtype)) {
    return "";
  }

  return `${entrancesLeft}/${ticket.entrances} wejść`;
};;

/**
 * Zwraca etykietę daty na podstawie typu biletu/karnetu
 */
export const getDateLabel = (ticket: Ticket): string => {
  return isTicketType(ticket) ? "Wykorzystaj do" : "Aktywny do";
};

/**
 * Zwraca sformatowaną datę ważności do wyświetlenia
 */
export const getFormattedValidToDate = (ticket: Ticket): string => {
  if (!ticket.validto) return "-";

  const dateObj =
    typeof ticket.validto === "number"
      ? new Date(ticket.validto)
      : parse(String(ticket.validto), "dd.MM.yyyy", new Date());

  return dateObj.toLocaleDateString("pl-PL");
};

/**
 * Zwraca sformatowaną datę ważności od
 */
export const getFormattedValidFromDate = (ticket: Ticket): string => {
  if (!ticket.validfrom) return "-";

  const dateObj =
    typeof ticket.validfrom === "number"
      ? new Date(ticket.validfrom)
      : parse(String(ticket.validfrom), "dd.MM.yyyy", new Date());

  return dateObj.toLocaleDateString("pl-PL");
};

/**
 * Zwraca informację o statusie biletu dla alertu
 */
export const getTicketStatusForAlert = (ticket: Ticket): { type: "success" | "warning" | "error"; message: string } => {
  if (isTicketExpired(ticket)) {
    return { type: "error", message: "Bilet wygasł" };
  }

  const entrancesLeft = ticket.entrances - (ticket.exercises?.length || 0);

  if (isOpenPass(ticket.passtype)) {
    return { type: "success", message: "Aktywny - bez limitu" };
  }

  if (entrancesLeft <= 0) {
    return { type: "warning", message: "Brak wejść" };
  }

  return { type: "success", message: `Pozostało wejść: ${entrancesLeft}` };
};

/**
 * Zwraca typ biletu do wyświetlenia
 */
export const getDisplayTicketType = (ticket: Ticket): string => {
  return getPassTypeLabel(ticket);
};

/**
 * Zwraca kolor dla EAInfoTile gdy bilet jest wygasły
 */
export const getInfoTileColorForExpired = (
  pass: Ticket,
  isExpired: boolean
): "error" | "warning" | "primary" | "gray" => {
  if (isExpired) {
    return "gray";
  }
  return getInfoTileColor(pass);
};

/**
 * Zwraca procent pozostałego czasu ważności (0-100)
 * Oblicza: (teraz - validFrom) / (validTo - validFrom) * 100
 * Jeśli 30% pozostało, funkcja zwraca 30
 */
export const calculateRemainingValidityPercent = (ticket: Ticket): number => {
  if (!ticket.validto) return 100;

  const now = getStartOfDay(new Date());
  const validFromDate = getDateFromTicketField(ticket.validfrom);
  const validToDate = getDateFromTicketField(ticket.validto);

  if (validToDate < now) return 0; // Już wygasł

  const totalDays = Math.max(1, differenceInDays(validToDate, validFromDate));
  const remainingDays = differenceInDays(validToDate, now);

  return Math.max(0, Math.round((remainingDays / totalDays) * 100));
};

/**
 * Sprawdza czy karnet/bilet traci ważność (< 30% czasu pozostało)
 */
export const isValidityEndingSoon = (ticket: Ticket): boolean => {
  const remainingPercent = calculateRemainingValidityPercent(ticket);
  return remainingPercent < 30 && remainingPercent > 0;
};

/**
 * Sprawdza czy karnet/bilet wygasł (validto <= dzisiaj)
 */
export const isValidityEnded = (ticket: Ticket): boolean => {
  return calculateRemainingValidityPercent(ticket) === 0;
};

/**
 * Zwraca ilość pozostałych wejść
 * Dla biletów: entrances - exercises.length
 * Dla karnetów: entrances - exercises.length
 */
export const calculateRemainingEntrances = (ticket: Ticket): number => {
  const exercisesCount = ticket.exercises?.length || 0;
  return Math.max(0, ticket.entrances - exercisesCount);
};

/**
 * Sprawdza czy wejścia kończą się (<= 20% pozostało lub 0)
 * Tylko dla SELECT (passtype 2) i LIMITED (passtype 4) karnetów
 */
export const hasEntranceWarning = (ticket: Ticket): boolean => {
  // Tylko dla karnetów (ticket === false)
  if (isTicketType(ticket)) return false;

  // Tylko dla SELECT i LIMITED
  if (
    ticket.passtype !== PassType.CLASS_PASS_SINGLE &&
    ticket.passtype !== PassType.CLASS_PASS_MULTI &&
    ticket.passtype !== PassType.LIMITED
  ) {
    return false;
  }

  const remaining = calculateRemainingEntrances(ticket);
  const threshold = Math.ceil(ticket.entrances * 0.2); // 20%

  // Pokazuj warning gdy pozostało <= 20% wejść lub 0 wejść
  return remaining >= 0 && remaining <= threshold;
};

/**
 * Sprawdza czy bilet/karnet został już wykorzystany (brak wejść).
 * Dotyczy tylko produktów z limitowaną liczbą wejść.
 */
export const isTicketUsedUp = (ticket: Ticket): boolean => {
  // Status "wykorzystany" pokazujemy wyłącznie dla typu LIMITED, FLEXI, SZKÓŁKI ORAZ SELECT.
  // Dla SELECT liczymy wykorzystanie na podstawie statusów EXERCISE.
  // Ticket jest wykorzystany, jeśli wszystkie exercise mają status "Y".

  if (ticket.passtype === 2) {
    return (
      ticket.exercises.length > 0 &&
ticket.exercises.every((exercise) =>
  ['Y', 'U'].includes(exercise.status)
)    );
  }

  if (!isLimitedPass(ticket.passtype)) {
    return false;
  }

  const remaining = calculateRemainingEntrances(ticket);
  return ticket.entrances <= 0 || remaining <= 0;
};

/**
 * Zwraca kolor statusu dla biletów/karnetów
 * - 'error' (czerwony): wymagane działanie (brak terminu, koniec)
 * - 'warning' (pomarańczowy/żółty): ostrzeżenie (kończy się)
 * - 'primary' (zielony): OK
 * - 'gray': wygasły
 */
export const getStatusColorForTicket = (ticket: Ticket): "error" | "warning" | "primary" | "gray" => {
  // Jeśli wygasł
  if (isValidityEnded(ticket)) {
    return "gray";
  }

  // OPEN bilet bez terminu - BŁĄD (czerwony)
  if (isTicketType(ticket) && isOpenPass(ticket.passtype)) {
    if (!ticket.exercises || ticket.exercises.length === 0) {
      return "error";
    }
  }

  // SELECT bilet bez terminu - BŁĄD (czerwony)
  if (isTicketType(ticket) && ticket.passtype === PassType.CLASS_PASS_SINGLE) {
    if (!ticket.exercises || ticket.exercises.length === 0) {
      return "error";
    }
  }

  // SELECT karnet bez terminu - OSTRZEŻENIE (żółty)
  if (isPassType(ticket) && ticket.passtype === PassType.CLASS_PASS_SINGLE) {
    if (!ticket.exercises || ticket.exercises.length === 0) {
      return "warning";
    }
  }

  // SELECT karnet z mało wejściami - OSTRZEŻENIE (żółty)
  if (isPassType(ticket) && hasEntranceWarning(ticket)) {
    return "warning";
  }

  // Kończy się ważność - OSTRZEŻENIE
  if (isValidityEndingSoon(ticket)) {
    return "warning";
  }

  return "primary";
};

/**
 * Zwraca komunikat statusu dla wyświetlenia
 * Wspiera tłumaczenia poprzez funkcję `t`
 */
export const getStatusMessageForTicket = (ticket: Ticket, t?: any): string => {
  // Wygasł
  if (isValidityEnded(ticket)) {
    return t?.("wygasly_bilet") || "Bilet wygasł";
  }

  // OPEN bilet bez terminu
  if (isTicketType(ticket) && isOpenPass(ticket.passtype)) {
    if (!ticket.exercises || ticket.exercises.length === 0) {
      return t?.("wybierz_termin_bilet_open") || "Aby skorzystać z tego biletu, wybierz nowy termin";
    }
  }

  // SELECT bilet bez terminu + kończy się ważność
  if (
    isTicketType(ticket) &&
    ticket.passtype === PassType.CLASS_PASS_SINGLE &&
    (!ticket.exercises || ticket.exercises.length === 0) &&
    isValidityEndingSoon(ticket)
  ) {
    return "Czas się kończy, wybierz termin!";
  }

  // SELECT bilet bez terminu
  if (
    isTicketType(ticket) &&
    ticket.passtype === PassType.CLASS_PASS_SINGLE &&
    (!ticket.exercises || ticket.exercises.length === 0)
  ) {
    return t?.("wybierz_termin_bilet") || "Aby skorzystać z tego biletu, wybierz nowy termin";
  }

  // SELECT karnet bez terminu
  if (
    isPassType(ticket) &&
    ticket.passtype === PassType.CLASS_PASS_SINGLE &&
    (!ticket.exercises || ticket.exercises.length === 0)
  ) {
    return t?.("wybierz_termin_karnet") || "Karnet zajęciowy – wybierz termin";
  }

  // Karnet z mało wejściami
  if (isPassType(ticket) && hasEntranceWarning(ticket)) {
    return t?.("koncza_sie_wejscia") || "Kończą się wejścia";
  }

  // Kończy się ważność
  if (isValidityEndingSoon(ticket)) {
    const isPass = isPassType(ticket);
    if (isPass) {
      return t?.("konczy_sie_waznosc_karnetu") || "Kończy się ważność – wykorzystaj karnet";
    } else if (ticket.passtype === PassType.LIMITED) {
      return t?.("karnet_traci_waznosc") || "Karnet traci ważność – wykorzystaj wejścia";
    }
    return t?.("konczy_sie_waznosc") || "Kończy się ważność";
  }

  // OK
  return "";
};

/**
 * Obiekt podsumowujący cały status biletu/karnetu
 */
export interface TicketStatusSummary {
  color: 'error' | 'warning' | 'primary' | 'gray';
  message: string;
  isInactive: boolean;
  isExpired: boolean;
  isValidityEndingSoon: boolean;
  hasEntranceWarning: boolean;
  remainingEntrances: number;
  remainingValidityPercent: number;
}

/**
 * Zwraca pełne podsumowanie statusu biletu/karnetu
 */
export const getTicketStatusSummary = (ticket: Ticket, t?: any): TicketStatusSummary => {
  const isExpired = isValidityEnded(ticket);
  const isValidEnding = isValidityEndingSoon(ticket);
  const hasLowEntrances = hasEntranceWarning(ticket);
  const isInactive = isTicketInactive(ticket);
  
  return {
    color: getStatusColorForTicket(ticket),
    message: getStatusMessageForTicket(ticket, t),
    isInactive,
    isExpired,
    isValidityEndingSoon: isValidEnding,
    hasEntranceWarning: hasLowEntrances,
    remainingEntrances: calculateRemainingEntrances(ticket),
    remainingValidityPercent: calculateRemainingValidityPercent(ticket),
  };
};

/**
 * Helper: Zwraca datę ze zdania (liczba lub string)
 */
const getDateFromTicketField = (dateField: number | string | undefined): Date => {
  if (!dateField) return new Date();
  
  return typeof dateField === 'number'
    ? new Date(dateField)
    : parse(String(dateField), 'dd.MM.yyyy', new Date());
};

/**
 * Helper: Zwraca początek dnia (00:00)
 */
const getStartOfDay = (date: Date): Date => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

/**
 * Helper: Oblicza różnicę dni między dwoma datami
 */
const differenceInDays = (endDate: Date, startDate: Date): number => {
  const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};